/**
 * Reading a playbook's source documents — pure, no React, no I/O.
 *
 * A playbook is extracted from a SET of documents, but the response used to
 * carry one name (`source_document_name`), so the Details tab and the context
 * pane both drew a single file card however many went in. The backend now
 * returns `source_documents`; the legacy fields stay for older payloads.
 *
 * Everything that draws or streams a playbook's source files reads it through
 * here, so the fallback and the legacy-vs-real routing exist once.
 */

import { PlaybookListItem, PlaybookSourceDocument } from '../../types/playbook';
import { Attachment } from '../shared/attachments';

/** Enough of a playbook to answer "what was this extracted from". */
type SourceCarrier = Pick<
  PlaybookListItem,
  'source_document_name' | 'source_documents' | 'source_document_count'
>;

/**
 * The documents a playbook came from, newest field first.
 *
 * Older payloads have no `source_documents` at all: those synthesize the same
 * single legacy row the backend would have sent, so callers never branch on
 * the payload version — only on `is_legacy` per row.
 */
export const playbookSourceDocuments = (
  playbook: SourceCarrier | null | undefined,
): PlaybookSourceDocument[] => {
  if (!playbook) return [];

  const rows = playbook.source_documents;
  if (Array.isArray(rows) && rows.length > 0) {
    return rows.filter((row): row is PlaybookSourceDocument => !!row);
  }

  if (playbook.source_document_name) {
    return [
      {
        uuid: null,
        file_name: playbook.source_document_name,
        is_legacy: true,
      },
    ];
  }

  return [];
};

/** True when the playbook has anything to show under "Source Document(s)". */
export const hasPlaybookSourceDocuments = (
  playbook: SourceCarrier | null | undefined,
): boolean => playbookSourceDocuments(playbook).length > 0;

/**
 * How many documents to claim in a heading.
 *
 * `source_document_count` is the backend's own count and wins when present —
 * it stays right even if the list is ever paged — otherwise the list length.
 */
export const playbookSourceDocumentCount = (
  playbook: SourceCarrier | null | undefined,
): number => {
  const count = playbook?.source_document_count;
  if (typeof count === 'number' && Number.isFinite(count) && count > 0) return count;
  return playbookSourceDocuments(playbook).length;
};

/**
 * A source document as the shared attachment list draws it.
 *
 * The id has to survive a legacy row, which has no uuid — the fallback keys
 * off the index so React still gets a stable key. `raw` carries the original
 * row, which is what the view/download handlers route on.
 */
export const toSourceAttachment = (
  document: PlaybookSourceDocument,
  index = 0,
): Attachment => ({
  id: document.uuid || `playbook-source-${index}`,
  name: document.file_name || '',
  size:
    typeof document.file_size === 'number' && Number.isFinite(document.file_size)
      ? document.file_size
      : undefined,
  mime: document.file_type || undefined,
  uploadedBy: document.uploaded_by || undefined,
  uploadedAt: document.created_at || undefined,
  raw: document,
});

export const toSourceAttachments = (
  documents: PlaybookSourceDocument[],
): Attachment[] => documents.map((document, index) => toSourceAttachment(document, index));

/** The row an `Attachment` came from, back out of the list's callbacks. */
export const sourceDocumentOf = (
  attachment: Attachment | null | undefined,
): PlaybookSourceDocument | null => {
  const raw = attachment?.raw;
  return raw && typeof raw === 'object' ? (raw as PlaybookSourceDocument) : null;
};

/**
 * A legacy row streams through the playbook-wide endpoint (there is no
 * document to address); a real row streams by its own uuid.
 */
export const isLegacySourceDocument = (
  document: PlaybookSourceDocument | null | undefined,
): boolean => !document?.uuid;

/** "contract.pdf" for one, "contract.pdf +2 more" for a list. */
export const summarizeSourceDocuments = (
  documents: PlaybookSourceDocument[],
): string => {
  const names = documents.map((d) => (d.file_name || '').trim()).filter(Boolean);
  if (!names.length) return '';
  if (names.length === 1) return names[0];
  return `${names[0]} +${names.length - 1}`;
};
